import {
  Dropdown,
  DropdownItemTemplateType,
  DropdownProps,
} from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import { forwardRef } from 'react';

export type DropdownStyledProps = DropdownProps & {
  isDisabled?: boolean;
  options: unknown[];
  isLoading?: boolean;
  labelField?: string;
  clearable?: boolean;
  valueField?: string;
  id?: string;
  error?: boolean;
  isTitle?: boolean;
  absoluteLabel?: boolean;
  showRestricted?: boolean;
  fontClass?: string;
  borderColor?: string;
};

const DropdownStyled = forwardRef<Dropdown, DropdownStyledProps>(
  (
    {
      isDisabled = false,
      options,
      isLoading = false,
      labelField,
      valueField,
      clearable = true,
      id,
      error,
      isTitle,
      absoluteLabel,
      showRestricted,
      fontClass,
      borderColor,
      ...otherProps
    }: DropdownStyledProps,
    ref
  ) => {
    const selectedOptionTemplate = (
      option: DropdownItemTemplateType,
      props: DropdownProps
    ) => {
      const titleStyle = 'text-base lg:text-4xl navbarTitle';
      const absoluteStyle = 'absolute left-0 right-0 top-0';

      if (option) {
        return (
          <div
            className="relative"
            style={absoluteLabel ? { minHeight: '1.5rem' } : undefined}
          >
            <div
              className={
                isTitle
                  ? titleStyle
                  : `${absoluteLabel ? absoluteStyle : ''} blackText ${
                      fontClass ?? 'text-standard'
                    } overflow-hidden white-space-nowrap text-overflow-clip`
              }
            >
              {option[labelField as keyof DropdownItemTemplateType] || option}
            </div>
          </div>
        );
      }
      return (
        <span className={isTitle ? titleStyle : fontClass ?? 'text-standard'}>
          {props.placeholder ?? '\u00A0'}
        </span>
      );
    };

    const optionTemplate = (option: DropdownItemTemplateType) => {
      if (!option) {
        return null;
      }

      if (isLoading) {
        return (
          <div className="flex align-items-center p-2 disabled">
            <Skeleton height="1rem" />
          </div>
        );
      }

      return (
        <div className={`blackText ${fontClass ?? 'text-standard'}`}>
          {option[labelField as keyof DropdownItemTemplateType] || option}
        </div>
      );
    };

    return (
      <div className="relative w-full min-w-0">
        <Dropdown
          inputId={id}
          disabled={isDisabled}
          optionLabel={labelField}
          optionValue={valueField}
          options={
            isLoading
              ? [{ disabled: true }, { disabled: true }, { disabled: true }]
              : options
          }
          showClear={clearable}
          className={`fieldBorder max-w-full ${borderColor ?? ''} ${
            error && 'p-invalid'
          } ${isTitle && 'border-transparent'}`}
          itemTemplate={optionTemplate}
          valueTemplate={selectedOptionTemplate}
          ref={ref}
          {...otherProps}
        />
        {showRestricted && (
          <div className="absolute top-0 pl-3">
            <span className={`${fontClass ?? 'text-standard'} blackText`}>
              Restricted
            </span>
          </div>
        )}
      </div>
    );
  }
);

export default DropdownStyled;
